
import type { BlockRendererProps } from '@/lib/email-builder/block-registry';
import { parseBlockConfiguration } from '@/types/email-blocks';
import type { HeroBlockConfiguration } from '@/types/block-configurations';

const VERTICAL_ALIGN_MAP: Record<string, string> = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
};

export function HeroBlock({ block, isSelected, isHovered, onSelect, onHover }: BlockRendererProps) {
  const config = parseBlockConfiguration(block) as HeroBlockConfiguration;

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(block.id); }}
      onMouseEnter={(e) => { e.stopPropagation(); onHover(block.id); }}
      onMouseLeave={() => onHover(null)}
      className={`
        lw-block-hero cursor-pointer rounded transition-all overflow-hidden flex flex-col
        ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
        ${isHovered && !isSelected ? 'ring-1 ring-muted-foreground/50' : ''}
      `}
      style={{
        // Mirrors HeroBlockRenderer.cs (mj-hero): fixed height + vertical-align when a
        // height is set, fluid (grows with content) otherwise; radius only, no border
        height: config.minHeight ? `${config.minHeight}px` : undefined,
        justifyContent: config.minHeight
          ? VERTICAL_ALIGN_MAP[config.verticalAlign] || 'center'
          : undefined,
        backgroundColor: config.background?.color || '#1a1a2e',
        backgroundImage: config.background?.imageUrl ? `url(${config.background.imageUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        paddingTop: `${config.padding?.top ?? 40}px`,
        paddingRight: `${config.padding?.right ?? 20}px`,
        paddingBottom: `${config.padding?.bottom ?? 40}px`,
        paddingLeft: `${config.padding?.left ?? 20}px`,
        borderRadius: `${config.border?.radius ?? 0}px`,
      }}
    >
      <div
        className="flex flex-col gap-3"
        style={{
          textAlign: config.alignment?.horizontal || 'center',
          alignItems:
            config.alignment?.horizontal === 'left'
              ? 'flex-start'
              : config.alignment?.horizontal === 'right'
              ? 'flex-end'
              : 'center',
        }}
      >
        {config.title ? (
          <div
            style={{
              fontSize: `${config.titleTypography?.fontSize ?? 32}px`,
              fontWeight: config.titleTypography?.fontWeight || 'bold',
              color: config.titleTypography?.color || '#ffffff',
              fontFamily: config.titleTypography?.fontFamily || 'Arial',
              lineHeight: config.titleTypography?.lineHeight ?? 1.2,
            }}
          >
            {config.title}
          </div>
        ) : (
          <div className="text-2xl font-bold text-white/60">Hero Title</div>
        )}

        {config.subtitle && (
          <div
            style={{
              fontSize: `${config.subtitleTypography?.fontSize ?? 16}px`,
              fontWeight: config.subtitleTypography?.fontWeight || 'normal',
              color: config.subtitleTypography?.color || '#cccccc',
              fontFamily: config.subtitleTypography?.fontFamily || 'Arial',
              lineHeight: config.subtitleTypography?.lineHeight ?? 1.5,
            }}
          >
            {config.subtitle}
          </div>
        )}

        {config.ctaText && (
          <a
            href={config.ctaUrl || '#'}
            onClick={(e) => e.preventDefault()}
            className="inline-block rounded px-6 py-2 text-sm font-medium"
            style={{
              backgroundColor: config.ctaBackgroundColor || '#007bff',
              color: config.ctaTextColor || '#ffffff',
            }}
          >
            {config.ctaText}
          </a>
        )}

        {!config.title && !config.subtitle && !config.ctaText && (
          <p className="text-sm text-white/40 select-none">Configure hero content in properties</p>
        )}
      </div>
    </div>
  );
}
